import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, PartyPopper, X, Check, Loader2 } from 'lucide-react';
import confetti from 'canvas-confetti';
import { ShipperBasicInfoGate } from './ShipperBasicInfoGate';


interface VerificationStep {
  label: string;
  duration: number; // ms
}

const VERIFICATION_STEPS: VerificationStep[] = [
  { label: 'Connecting to FMCSA...', duration: 2000 },
  { label: 'Verifying MC Authority...', duration: 3000 },
  { label: 'Confirming DOT Number...', duration: 1500 },
  { label: 'Checking Operating Authority Status...', duration: 2500 },
  { label: 'Validating Insurance Records...', duration: 2000 },
  { label: 'Cross-referencing Business Records...', duration: 3000 },
  { label: 'Finalizing Account Setup...', duration: 2000 },
];

export const DocumentUploadGate: React.FC = () => {
  const { profile, user, hasActiveSubscription, refreshProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [completedSteps, setCompletedSteps] = useState<number>(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Clean up checkout URL params if present
  useEffect(() => {
    if (searchParams.has('checkout') || searchParams.has('session_id')) {
      searchParams.delete('checkout');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const shouldShow = hasActiveSubscription && profile?.documents_submitted === false;

  // Shippers get a basic-info form, not an FMCSA-themed animation.
  if (shouldShow && profile?.user_type === 'shipper') {
    return <ShipperBasicInfoGate />;
  }

  // Run the sequential verification animation
  useEffect(() => {
    if (!shouldShow || submitted) return;

    let cancelled = false;
    let totalDelay = 0;

    VERIFICATION_STEPS.forEach((step, i) => {
      totalDelay += step.duration;
      const delay = totalDelay;
      setTimeout(() => {
        if (!cancelled) setCompletedSteps(i + 1);
      }, delay);
    });

    // After all steps complete, auto-submit
    const finalDelay = totalDelay + 800; // small pause after last check
    const submitTimer = setTimeout(async () => {
      if (cancelled || !user) return;
      setSubmitted(true);
      try {
        await supabase
          .from('profiles')
          .update({ documents_submitted: true })
          .eq('id', user.id);
        setShowWelcome(true);
        // fire confetti
        const duration = 2000;
        const end = Date.now() + duration;
        const frame = () => {
          confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#00e5ff', '#6366f1', '#22c55e'] });
          confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#00e5ff', '#6366f1', '#22c55e'] });
          if (Date.now() < end) requestAnimationFrame(frame);
        };
        frame();
      } catch (err) {
        console.error('Error updating documents_submitted:', err);
      }
    }, finalDelay);

    return () => {
      cancelled = true;
      clearTimeout(submitTimer);
    };
  }, [shouldShow, submitted, user]);

  if (!shouldShow && !showWelcome) return null;

  const handleDismissWelcome = async () => {
    await refreshProfile();
    setShowWelcome(false);
  };

  // ---------- Welcome screen (unchanged) ----------
  if (showWelcome) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-background/10 backdrop-blur-[1px]" />
        <div className="relative w-full max-w-md mx-4 rounded-2xl border bg-card shadow-2xl p-8 flex flex-col items-center text-center gap-5 animate-in fade-in zoom-in-95 duration-300">
          <button
            onClick={handleDismissWelcome}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center">
            <PartyPopper className="w-8 h-8 text-accent" />
          </div>

          <div>
            <h2 className="text-xl font-bold text-foreground mb-2">Welcome to LaneRunner! 🎉</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your documents have been submitted and are under review.
              In the meantime, your dashboard is fully unlocked — start moving freight!
            </p>
          </div>

          <Button
            onClick={handleDismissWelcome}
            className="w-full h-11 text-sm font-semibold"
            size="lg"
          >
            Let's go →
          </Button>
        </div>
      </div>
    );
  }

  // ---------- AI Verification loading screen ----------
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-background/10 backdrop-blur-[1px]" />

      <div className="relative w-full max-w-lg mx-4 rounded-2xl border bg-card shadow-2xl p-6 flex flex-col gap-5 animate-in fade-in zoom-in-95 duration-300">
        <div className="absolute -top-px left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center mb-3">
            <Shield className="w-7 h-7 text-accent" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Verifying Your Account</h2>
          <p className="text-sm text-muted-foreground mt-1">
            We're running automated checks on your credentials. This will take just a moment.
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          {VERIFICATION_STEPS.map((step, i) => {
            const isComplete = i < completedSteps;
            const isActive = i === completedSteps;

            return (
              <div
                key={step.label}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
                  isComplete
                    ? 'bg-success/5 border-success/30'
                    : isActive
                    ? 'bg-accent/5 border-accent/30'
                    : 'bg-muted/20 border-transparent opacity-50'
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                  isComplete
                    ? 'bg-success/15'
                    : isActive
                    ? 'bg-accent/15'
                    : 'bg-muted/30'
                }`}>
                  {isComplete ? (
                    <Check className="w-4 h-4 text-success" />
                  ) : isActive ? (
                    <Loader2 className="w-4 h-4 text-accent animate-spin" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                  )}
                </div>
                <span className={`text-sm font-medium transition-colors duration-300 ${
                  isComplete
                    ? 'text-success'
                    : isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 rounded-full bg-muted/30 overflow-hidden">
          <div
            className="h-full bg-accent rounded-full transition-all duration-700 ease-out"
            style={{ width: `${(completedSteps / VERIFICATION_STEPS.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};
